import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { Button, ConfirmDialog, Select, Toast } from '@/components/ui';

/**
 * Deleting old leads.
 *
 * The one irreversible thing on the leads page — everything else there can be
 * undone by re-importing — so it counts first and makes somebody read the
 * number before the button does anything.
 *
 * The server keeps its own floor on the age and its own rules about what is
 * never deleted (anything on do-not-call, anything called recently). This
 * offers a short list of ages rather than a free text box for the same
 * reason: the failure that matters is somebody typing 3 when they meant 300.
 */

const AGES: Array<{ value: number; label: string }> = [
  { value: 90, label: 'older than 3 months' },
  { value: 180, label: 'older than 6 months' },
  { value: 365, label: 'older than a year' },
  { value: 730, label: 'older than 2 years' },
];

interface Counted {
  dry_run: boolean;
  matched: number;
  deleted?: number;
  kept: string;
}

export default function LeadPurge({
  path = '/leads/purge',
  tenantId,
  queryKey = 'leads',
}: {
  /** The client path by default; the admin one takes a tenant. */
  path?: string;
  tenantId?: string;
  queryKey?: string;
}) {
  const qc = useQueryClient();
  const [days, setDays] = useState(365);
  const [counted, setCounted] = useState<Counted | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  const body = (dryRun: boolean) => ({
    older_than_days: days,
    dry_run: dryRun,
    ...(tenantId ? { tenant_id: tenantId } : {}),
  });

  const count = useMutation({
    mutationFn: () => api.post<Counted>(path, body(true)),
    onSuccess: (result) => setCounted(result),
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not count them'), tone: 'bad' }),
  });

  const purge = useMutation({
    mutationFn: () => api.post<Counted>(path, body(false)),
    onSuccess: (result) => {
      setCounted(null);
      void qc.invalidateQueries({ queryKey: [queryKey] });
      setToast({
        message: `Deleted ${(result.deleted ?? 0).toLocaleString()} leads.`,
        tone: 'good',
      });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not delete them'), tone: 'bad' }),
  });

  return (
    <>
      <div className="flex items-center gap-2">
        <Select
          value={String(days)}
          onChange={(e) => setDays(Number(e.target.value))}
          className="w-44 text-xs"
          aria-label="How old"
        >
          {AGES.map((age) => (
            <option key={age.value} value={age.value}>
              {age.label}
            </option>
          ))}
        </Select>
        <Button
          variant="secondary"
          size="sm"
          loading={count.isPending}
          disabled={!!tenantId === false && path.startsWith('/admin')}
          onClick={() => count.mutate()}
        >
          Delete old leads
        </Button>
      </div>

      <ConfirmDialog
        open={!!counted}
        title={
          counted?.matched
            ? `Delete ${counted.matched.toLocaleString()} leads?`
            : 'Nothing to delete'
        }
        confirmLabel={counted?.matched ? `Delete ${counted.matched}` : 'Close'}
        // Typed confirmation, because this is the one thing here that cannot
        // be undone by re-importing.
        requireTyping={counted?.matched ? String(counted.matched) : undefined}
        busy={purge.isPending}
        onCancel={() => setCounted(null)}
        onConfirm={() => (counted?.matched ? purge.mutate() : setCounted(null))}
        body={
          <div className="space-y-2 text-sm">
            {counted?.matched ? (
              <>
                <p>
                  <strong>{counted.matched.toLocaleString()}</strong> leads
                  match. This cannot be undone — they would have to be
                  re-imported from the dialer.
                </p>
                <p className="text-xs text-ink-500">{counted.kept}</p>
              </>
            ) : (
              <p className="text-xs text-ink-500">
                No leads match that age. {counted?.kept}
              </p>
            )}
          </div>
        }
      />

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </>
  );
}
